import { useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { motion, AnimatePresence } from "framer-motion";

import { assignTechnician } from "../../api/tasks";

export default function AssignTechnicianModal({
  open,
  onClose,
  taskId,
  technicians = [],
  onAssigned,
}) {
  const [search, setSearch] = useState("");
  const [submitError, setSubmitError] = useState("");

  const filteredTechnicians = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return technicians;

    return technicians.filter((t) => {
      const fullName = `${t.firstName || ""} ${t.lastName || ""}`.trim().toLowerCase();
      const email = (t.email || "").toLowerCase();
      const phone = (t.phoneNumber || t.phone || "").toLowerCase();
      return (
        fullName.includes(q) ||
        email.includes(q) ||
        phone.includes(q)
      );
    });
  }, [technicians, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="flex min-h-full items-start justify-center px-4 py-6 sm:py-10">
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 max-h-[calc(100vh-3rem)]"
        >
          {/* Header */}
          <div className="shrink-0 flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
            <div>
              <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Assign Technician
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Search and select a technician to assign to this task.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              ×
            </button>
          </div>

          <Formik
            initialValues={{ technicianId: "" }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              setSubmitError("");

              try {
                await assignTechnician(taskId, Number(values.technicianId));
                onAssigned?.();
                resetForm();
                setSearch("");
                onClose?.();
              } catch (err) {
                console.error("ASSIGN FAILED", err);
                setSubmitError("Failed to assign technician.");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => {
              const selectedTechnician = technicians.find(
                (t) => String(t.id) === String(values.technicianId)
              );

              return (
                <Form className="flex min-h-0 flex-1 flex-col">
                  {/* Body */}
                  <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <div className="space-y-5">
                      {submitError && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                          {submitError}
                        </div>
                      )}

                      {/* Search */}
                      <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                            Technician Search
                          </h4>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Find a technician by name, email, or phone number.
                          </p>
                        </div>

                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                            🔎
                          </span>

                          <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search technicians..."
                            className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                          />
                        </div>
                      </section>

                      {/* Selected summary */}
                      <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="mb-4 flex items-center justify-between gap-4">
                          <div>
                            <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                              Available Technicians
                            </h4>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              Choose one technician from the list below.
                            </p>
                          </div>

                          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {filteredTechnicians.length} found
                          </div>
                        </div>

                        {selectedTechnician && (
                          <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/30">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                              Selected Technician
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                              {selectedTechnician.firstName} {selectedTechnician.lastName}
                            </p>
                            {(selectedTechnician.email || selectedTechnician.phoneNumber || selectedTechnician.phone) && (
                              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {selectedTechnician.email ||
                                  selectedTechnician.phoneNumber ||
                                  selectedTechnician.phone}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="max-h-80 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/50 p-2 dark:border-slate-800 dark:bg-slate-950/30">
                          <AnimatePresence mode="popLayout">
                            {filteredTechnicians.length === 0 ? (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex flex-col items-center justify-center py-10 text-center"
                              >
                                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-xl dark:bg-slate-800">
                                  👨‍🔧
                                </div>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  No technicians found
                                </p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  Try a different search term.
                                </p>
                              </motion.div>
                            ) : (
                              <div className="space-y-2">
                                {filteredTechnicians.map((t) => {
                                  const isSelected =
                                    String(values.technicianId) === String(t.id);

                                  return (
                                    <label
                                      key={t.id}
                                      className={`flex cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 transition ${
                                        isSelected
                                          ? "border-slate-900 bg-slate-50 dark:border-white dark:bg-slate-800/60"
                                          : "border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-800 dark:hover:bg-slate-800/40"
                                      }`}
                                    >
                                      <div className="flex min-w-0 items-center gap-3">
                                        <input
                                          type="radio"
                                          name="technicianId"
                                          value={t.id}
                                          checked={isSelected}
                                          onChange={() =>
                                            setFieldValue("technicianId", String(t.id))
                                          }
                                          className="h-4 w-4 border-slate-300 text-black focus:ring-2 focus:ring-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:focus:ring-slate-700"
                                        />

                                        <div className="min-w-0">
                                          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                                            {t.firstName} {t.lastName}
                                          </p>

                                          {(t.email || t.phoneNumber || t.phone) && (
                                            <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                              {t.email ||
                                                t.phoneNumber ||
                                                t.phone}
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      {isSelected && (
                                        <span className="ml-3 shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-white dark:text-black">
                                          Selected
                                        </span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </AnimatePresence>
                        </div>
                      </section>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting || !values.technicianId}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
                    >
                      {isSubmitting && (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
                      )}
                      {isSubmitting ? "Assigning..." : "Assign Technician"}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </motion.div>
      </div>
    </div>
  );
}